import { useStore } from 'effector-react'
import React, { FC } from 'react'
import styled from 'styled-components'

import { getProjectName } from '../../../lib/get-project-name'
import { objectHasProperty } from '../../../lib/typescript-helpers'
import { useAVNConfig } from '../../../stages/AVN'
import { Icon } from '../../../ui/icon'
import { Tooltip } from '../../../ui/tooltip'
import { $declaration, $filterValues, FilterSection, removeValues, resetValues } from '../../filter/model'

type FilterItem = { label: string; key: string; sectionKey: string; tooltip?: string }

const createFilterItem = (key: any, section?: FilterSection) => {
  if (section) {
    for (let i = 0; i <= section.groups.length; i++) {
      if (section.type === 'range') {
        const label = !isNaN(key.value) && key.value % 1 > 0 ? key.value.toFixed(1) : key.value
        if (key.range === 'max') {
          return getObjectForFilter('до: ' + label, key, section.key, '')
        } else {
          return getObjectForFilter('от: ' + label, key, section.key, '')
        }
      } else {
        const item = section?.groups[i]?.fields.find((elem) => {
          return objectHasProperty(elem, 'value') ? elem.value === key : null
        })
        if (item) {
          const label = objectHasProperty(item, 'label') ? item.label : ''
          const colorName = objectHasProperty(item, 'colorName') ? item.colorName : ''
          const labelForSelectedFilter = label || colorName || ''
          // @ts-ignore
          return getObjectForFilter(labelForSelectedFilter, item?.value, section.key, item.tooltip)
        }
      }
    }
  }
  return getObjectForFilter('', '', '', '')
}

const getObjectForFilter = (label: string, key: string, sectionKey: any, tooltip: string) => {
  return {
    label,
    key,
    sectionKey,
    tooltip
  }
}

const getObjectForFilterRange = (range: string, value: string | number, title: string) => {
  const convertValue = returnsConvertsPriceOrReturnsValue(title, value)
  return {
    range,
    value: convertValue,
    title
  }
}

const returnsConvertsPriceOrReturnsValue = (title: string, value: string | number) => {
  let convertString
  if (title === 'Цена') {
    convertString = value.toLocaleString() + ' ₽'
  } else if (title === 'Мощность двигателя (л.с.)') {
    convertString = value + ' л.с.'
  } else {
    convertString = value
  }
  return convertString
}

export const SelectedFilters: FC = () => {
  const props = useAVNConfig()
  const storeSelectedFilters = useStore($filterValues)
  const declaration = useStore($declaration)
  let currentFilters: FilterItem[] = []

  if (!storeSelectedFilters || declaration === null) return null
  for (let key in storeSelectedFilters) {
    const section = declaration.sections.find((elem: { key: string }) => {
      return elem.key === key
    })
    if (
      section &&
      objectHasProperty(section.groups[0].fields[0], 'min') &&
      objectHasProperty(section.groups[0].fields[0], 'max')
    ) {
      if (storeSelectedFilters[key][0] > Number(section.groups[0].fields[0].min)) {
        const valueMin: object = getObjectForFilterRange('min', storeSelectedFilters[key][0], section.title)
        currentFilters.push(createFilterItem(valueMin, section))
      }
      if (storeSelectedFilters[key][1] < Number(section.groups[0].fields[0].max)) {
        const valueMax: object = getObjectForFilterRange('max', storeSelectedFilters[key][1], section.title)
        currentFilters.push(createFilterItem(valueMax, section))
      }
    } else {
      storeSelectedFilters[key].forEach((elem) => {
        return currentFilters.push(createFilterItem(elem, section))
      })
    }
  }

  const deleteFilter = (key: any, sectionKey: string) => {
    if (key && key.range) {
      removeValues({ [sectionKey]: [key.range] })
    } else {
      removeValues({ [sectionKey]: [key] })
    }
  }

  const countFilterForButtonReset = currentFilters.filter((item) => item.key !== '').length
  return (
    <SelectedFilterInner>
      {Object.values(currentFilters).map((item) => {
        return objectHasProperty(item, 'key') && item.key !== '' ? (
          <>
            <PickedFilter key={item.key + item.label} data-for="label" data-tip={item?.tooltip}>
              {objectHasProperty(item, 'label') ? item.label : ''}
              <Delete onClick={() => deleteFilter(item.key, item.sectionKey)}>
                <Icon name={'cancel'} size={1} />
              </Delete>
            </PickedFilter>
            {props.isShowTooltipInFilter && <Tooltip id="label" multiline={true} />}
          </>
        ) : null
      })}
      {countFilterForButtonReset >= 2 ? (
        <ResetSelectedFilter onClick={() => resetValues()}>{props.i18n.resetAllFilter}</ResetSelectedFilter>
      ) : null}
    </SelectedFilterInner>
  )
}

const SelectedFilterInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`

const PickedFilter = styled.div`
  ${({ theme }) => theme.typography.paragraph.p1}
  height: ${({ theme }) => theme.spacing[10]};
  padding: 0 ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.palette.white};
  font-size: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.palette.brand.primary};
  display: flex;
  align-items: center;
  margin: 0 ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]} 0;
  //font-family: BMWType, 'Roboto', 'Helvetica Neue', sans-serif;
  border: 1px solid #ebebeb;
  border-radius: ${() => (getProjectName() === 'avito' ? '6px' : 0)};
`

const Delete = styled.div`
  width: ${({ theme }) => theme.spacing[3]};
  height: ${({ theme }) => theme.spacing[3]};
  margin-left: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ResetSelectedFilter = styled.div`
  ${({ theme }) => theme.typography.paragraph.p1}
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.spacing[4]};
  padding: 0 ${({ theme }) => theme.spacing[2]};
  margin: 0 0 ${({ theme }) => theme.spacing[3]} 0;
  height: ${({ theme }) => theme.spacing[10]};
  cursor: pointer;
  color: ${({ theme }) => theme.palette.black};
  font-family: BMWType, 'Roboto', 'Helvetica Neue', sans-serif;
`
